import { handleActions } from 'redux-actions';
import {
  setSystemMessages,
  setAdminMessages,
  setScheduledMessages,
  setMessagesPage,
  MESSAGES_LIST_KEY,
  MESSAGES_DEFAULT_PAGE_SIZE,
  setLastAdminMessageId,
  createMessageSuccess,
  requestFail,
  togglePushMessagesModal,
  toggleFilterSettingsModal,
  setCouplingTableData,
  setSelectedFilters,
  refreshFilters,
  normalizeMessagesSort,
  setDonorMessages,
  clearDonorMessages,
} from './actions';
import { EMPTY_FILTERS, normalizeFilters } from './filters';
import { SET_SEARCH_PNR } from '../users/actionTypes';
import { SET_CURRENT_BLOODBANK, LOGOUT_REQUEST } from '../auth/actionTypes';

// No donor search result: `uid` null means "nothing loaded" — the screen
// compares `uid` AND `bloodbankId` with the donor found by the PNR lookup
// and the bank on screen before showing rows.
export const EMPTY_DONOR_MESSAGES = { uid: null, bloodbankId: null, content: [], truncated: false, error: null };

const initialPaging = (type) => ({
  page: 1,
  pageSize: MESSAGES_DEFAULT_PAGE_SIZE,
  total: 0,
  totalPages: 0,
  bloodbankId: null,
  sort: normalizeMessagesSort(type).key,
  order: normalizeMessagesSort(type).order,
});

export const initialState = {
  systemMessages: [],
  adminMessages: [],
  scheduledMessages: [],
  // per type: the server page currently shown (1-based), its size, the
  // server's totals for the pager and the sort it was served in;
  // bloodbankId scopes the list
  messagesPaging: {
    admin: initialPaging('admin'),
    system: initialPaging('system'),
    scheduled: initialPaging('scheduled'),
  },
  // the PNR search result: every message of ONE donor (all types, no
  // paging); cleared when the search is cleared, the bank changes or the
  // user logs out
  donorMessages: EMPTY_DONOR_MESSAGES,
  lastAdminMessageId: null,
  selectedFilters: EMPTY_FILTERS,
  couplingTableData: [],
  isError: false,
  error: null,
  isToastErrorShown : false,
  pushMessagesModalIsOpen: false,
  filterSettingsModalIsOpen: false,
};

const pushMessagesReducer = handleActions({
  [setSystemMessages]: (state, action) => ({
    ...state,
    systemMessages: action.payload.content,
    isError: false,
    error: null,
    isToastErrorShown: false,
  }),
  [setAdminMessages]: (state, action) => ({
    ...state,
    adminMessages: action.payload.content,
    isError: false,
    error: null,
    isToastErrorShown: false,
  }),
  [setScheduledMessages]: (state, action) => ({
    ...state,
    scheduledMessages: action.payload.content,
    isError: false,
    error: null,
    isToastErrorShown: false,
  }),
  // One handler for every history page: the fetched server page REPLACES
  // the type's list (no client-side accumulation), and the pager state is
  // taken from the server's `paging` object, falling back to what the thunk
  // requested when the server omits a field.
  [setMessagesPage]: (state, action) => {
    const { type, bloodbankId, content, paging: serverPaging, requested } = action.payload;
    const key = MESSAGES_LIST_KEY[type];
    if (!key) return state;
    // The store is created with a persisted subset of this slice (services/
    // store.js saves only selectedFilters and merges it over initialState),
    // but never assume messagesPaging (or the lists) exist on `state`.
    const allPaging = state.messagesPaging || initialState.messagesPaging;
    const previous = allPaging[type] || initialState.messagesPaging[type];
    const asked = requested || {};
    const server = serverPaging || {};
    const num = (...candidates) => {
      const hit = candidates.find((v) => typeof v === 'number' && Number.isFinite(v));
      return hit === undefined ? null : hit;
    };
    const pageSize = num(server.page_size, asked.pageSize, previous.pageSize, MESSAGES_DEFAULT_PAGE_SIZE);
    const total = num(server.total, 0);
    const totalPages = num(server.total_pages, pageSize > 0 ? Math.ceil(total / pageSize) : 0);
    const page = num(server.page, asked.page, 1);
    // the sort the page was served in: the server's echo wins, then the
    // request, then the tab default (never an unsupported key)
    const sorting = normalizeMessagesSort(
      type,
      typeof server.sort === 'string' ? server.sort : asked.sort,
      typeof server.order === 'string' ? server.order : asked.order,
    );
    return {
      ...state,
      [key]: Array.isArray(content) ? content : [],
      messagesPaging: {
        ...allPaging,
        [type]: { page, pageSize, total, totalPages, bloodbankId, sort: sorting.key, order: sorting.order },
      },
      isError: false,
      error: null,
      isToastErrorShown: false,
    };
  },
  [setDonorMessages]: (state, action) => {
    const { uid, bloodbankId, content, truncated, error } = action.payload || {};
    return {
      ...state,
      donorMessages: {
        uid: uid || null,
        bloodbankId: bloodbankId || null,
        content: Array.isArray(content) ? content : [],
        truncated: Boolean(truncated),
        error: error || null,
      },
    };
  },
  [clearDonorMessages]: (state) => ({ ...state, donorMessages: EMPTY_DONOR_MESSAGES }),
  // the shared PNR search box cleared (users slice owns the text): a changed
  // non-empty PNR is NOT cleared here — the screen refetches for the new
  // donor and shows nothing until that result's uid + bank match
  [SET_SEARCH_PNR]: (state, action) => (
    action.payload ? state : { ...state, donorMessages: EMPTY_DONOR_MESSAGES }
  ),
  [SET_CURRENT_BLOODBANK]: (state) => ({ ...state, donorMessages: EMPTY_DONOR_MESSAGES }),
  [LOGOUT_REQUEST]: (state) => ({ ...state, donorMessages: EMPTY_DONOR_MESSAGES }),
  [setLastAdminMessageId]: (state, action) => ({
    ...state,
    lastAdminMessageId: +action.payload.content.id + 1,
    isError: false,
    error: null,
    isToastErrorShown: false,
  }),
  [createMessageSuccess]: (state) => ({
    ...state,
    selectedFilters: EMPTY_FILTERS,
    isError: false,
    error: null,
    isToastErrorShown: false,
  }),
  [requestFail]: (state, action) => ({
    ...state,
    isError: true,
    error: action.payload,
    isToastErrorShown: true,
  }),
  [togglePushMessagesModal]: (state) => ({
    ...state,
    pushMessagesModalIsOpen: !state.pushMessagesModalIsOpen,
  }),
  [toggleFilterSettingsModal]: (state) => ({
    ...state,
    filterSettingsModalIsOpen: !state.filterSettingsModalIsOpen,
  }),
  [setCouplingTableData]: (state, action) => ({
    ...state,
    couplingTableData: action.payload.content,
    isError: false,
    error: null,
    isToastErrorShown: false,
  }),
  // every publisher goes through the canonical 10-key shape (filters.js)
  [setSelectedFilters]: (state, action) => ({
    ...state,
    selectedFilters: normalizeFilters(action.payload),
  }),
  [refreshFilters]: (state) => ({
    ...state,
    selectedFilters: EMPTY_FILTERS,
  }),
},
initialState);

export default pushMessagesReducer;